import apiClient from './apiClient';
import objektkostenService from './objektkostenService';
import anlageVCacheService from './anlageVCacheService';
import statistikCacheService from './statistikCacheService';

/**
 * Cache für Darlehen-Daten mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * Pattern analog zu propertyCacheService.js, aber mit:
 * - Zwei separaten Cache-Bereichen: Loans (Stammdaten, 15min TTL) und
 *   Payments (volatiler durch Sondertilgungs-Recalc, 5min TTL)
 * - Expliziter Invalidierung nach Mutationen
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 */

// ───────────────────────── Loans (Stammdaten) ─────────────────────────────

const CACHE_VERSION = 'v2';
const CACHE_PREFIX = `loans:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 Minuten (Stammdaten)

const PAYMENTS_CACHE_PREFIX = `loan-payments:${CACHE_VERSION}:`;
const PAYMENTS_TTL_MS = 5 * 60 * 1000; // 5 Minuten (volatiler durch Recalc)

// In-Memory Caches
const memoryCache = new Map();
const paymentsMemoryCache = new Map();

// Pending Requests (Stampede-Schutz)
const pendingRequests = new Map();
const paymentsPendingRequests = new Map();

// Invalidation-Tokens für Race-Condition-Schutz
let cacheInvalidationToken = 0;
let paymentsCacheInvalidationToken = 0;

// ──────────────────────── localStorage Helpers ────────────────────────────

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__loans_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

/**
 * Entfernt Cache-Einträge fremder Versionen (z. B. v1 ohne version-Feld / mit bezahlt-Feldern) einmalig
 * beim Modul-Init (Muster statistikCacheService).
 */
const removeLegacyCacheVersions = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      const isLoans = key.startsWith('loans:') && !key.startsWith(CACHE_PREFIX);
      const isPayments = key.startsWith('loan-payments:') && !key.startsWith(PAYMENTS_CACHE_PREFIX);
      if (isLoans || isPayments) localStorage.removeItem(key);
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};
removeLegacyCacheVersions();

const getFromLocalStorage = (prefix, key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(prefix + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const setToLocalStorage = (prefix, key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(prefix + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (prefix, key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(prefix + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Löscht alle Cache-Einträge mit gegebenem Prefix aus localStorage.
 */
const clearLocalStorageCacheByPrefix = (prefix) => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

// ──────────────────────── Shared Helpers ──────────────────────────────────

const isFresh = (entry, ttlMs) => {
  return !!entry && Date.now() - entry.timestamp < ttlMs;
};

/**
 * Gibt eine defensive Kopie gecachter Daten zurück.
 * Unterstützt Arrays und Page-Objekte { content: [...], totalElements, ... }.
 * Verhindert, dass Consumer den internen Cache korrumpieren.
 * @param {Array|object} data
 * @returns {Array|object}
 */
const cloneCachedData = (data) => {
  if (Array.isArray(data)) return [...data];
  if (data && typeof data === 'object' && Array.isArray(data.content)) {
    return { ...data, content: [...data.content] };
  }
  return data;
};

const invalidateCacheKey = (memory, pending, prefix, key) => {
  memory.delete(key);
  pending.delete(key);
  removeFromLocalStorage(prefix, key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

// ──────────────────────── Generic getCached ───────────────────────────────

/**
 * Generische Cache-Funktion für GET-Requests.
 * @param {string} url - API-Pfad
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {Map} memory - Memory-Cache Map
 * @param {Map} pending - Pending-Requests Map
 * @param {string} prefix - localStorage-Prefix
 * @param {number} ttlMs - Time-to-Live in Millisekunden
 * @param {object} tokenRef - { current: number } für Invalidation-Token
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<Array|object>}
 */
const getCached = (url, params, signal, memory, pending, prefix, ttlMs, tokenRef, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memory.get(cacheKey);
  if (isFresh(memoryCached, ttlMs)) {
    return Promise.resolve(cloneCachedData(memoryCached.data));
  }
  if (memoryCached) {
    memory.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(prefix, cacheKey);
  if (isFresh(localCached, ttlMs)) {
    memory.set(cacheKey, localCached);
    return Promise.resolve(cloneCachedData(localCached.data));
  }
  if (localCached) {
    removeFromLocalStorage(prefix, cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pending.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pending.get(cacheKey).then(data => cloneCachedData(data)),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;

  // ETag senden falls vorhanden (Revalidierung)
  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = tokenRef.current;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache()/invalidate aufgerufen wurde
    if (currentToken !== tokenRef.current) {
      pending.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, memory, pending, prefix, ttlMs, tokenRef, _retryCount + 1);
        }
        return cloneCachedData(response.data || []);
      }
      return cloneCachedData(response.data);
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      if (!staleEntry) {
        pending.delete(cacheKey);
        invalidateCacheKey(memory, pending, prefix, cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, memory, pending, prefix, ttlMs, tokenRef, _retryCount + 1);
        }
        return [];
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memory.set(cacheKey, refreshedEntry);
      setToLocalStorage(prefix, cacheKey, refreshedEntry);
      pending.delete(cacheKey);
      return staleEntry.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memory.set(cacheKey, newEntry);
    setToLocalStorage(prefix, cacheKey, newEntry);
    pending.delete(cacheKey);
    return data;
  }).catch(err => {
    pending.delete(cacheKey);
    throw err;
  });

  pending.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => cloneCachedData(data)),
    signal
  );
};

// ──────────────────────── Token-Refs (für getCached) ─────────────────────

// Wrapping in a ref-like object so getCached can read the current token value
const loansTokenRef = { get current() { return cacheInvalidationToken; } };
const paymentsTokenRef = { get current() { return paymentsCacheInvalidationToken; } };

// ──────────────────────── Loans Invalidations ────────────────────────────

const invalidateLoansList = () => {
  cacheInvalidationToken++;
  for (const key of [...memoryCache.keys()]) {
    if (key === '/loans' || key.startsWith('/loans?')) {
      memoryCache.delete(key);
      pendingRequests.delete(key);
      removeFromLocalStorage(CACHE_PREFIX, key);
    }
  }
  if (storageAvailable) {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(CACHE_PREFIX)) {
          const cacheKey = storageKey.substring(CACHE_PREFIX.length);
          if (cacheKey === '/loans' || cacheKey.startsWith('/loans?')) {
            localStorage.removeItem(storageKey);
          }
        }
      }
    } catch {
      // Ignorieren
    }
  }
};

const invalidateLoansDetail = (id) => {
  cacheInvalidationToken++;
  const detailKey = `/loans/${id}`;
  invalidateCacheKey(memoryCache, pendingRequests, CACHE_PREFIX, detailKey);
};

const invalidateLoanPayments = (loanId) => {
  paymentsCacheInvalidationToken++;
  // Alle Payment-Einträge dieses Loans löschen (mit und ohne Query-Params)
  const prefix = `/loans/${loanId}/payments`;
  for (const key of [...paymentsMemoryCache.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}?`) || key.startsWith(`${prefix}/`)) {
      paymentsMemoryCache.delete(key);
      paymentsPendingRequests.delete(key);
      removeFromLocalStorage(PAYMENTS_CACHE_PREFIX, key);
    }
  }
  if (storageAvailable) {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(PAYMENTS_CACHE_PREFIX)) {
          const cacheKey = storageKey.substring(PAYMENTS_CACHE_PREFIX.length);
          if (cacheKey === prefix || cacheKey.startsWith(`${prefix}?`) || cacheKey.startsWith(`${prefix}/`)) {
            localStorage.removeItem(storageKey);
          }
        }
      }
    } catch {
      // Ignorieren
    }
  }
};

/**
 * Bei 409 (Versionskonflikt/Sperre) ist der Zahlungs-Cache des Darlehens veraltet.
 */
const invalidatePaymentsOnConflict = (loanId, err) => {
  if (err?.response?.status === 409) invalidateLoanPayments(loanId);
};

const invalidateLoanDocuments = (loanId) => {
  cacheInvalidationToken++;
  const docsKey = `/loans/${loanId}/documents`;
  for (const key of [...memoryCache.keys()]) {
    if (key === docsKey || key.startsWith(`${docsKey}?`)) {
      invalidateCacheKey(memoryCache, pendingRequests, CACHE_PREFIX, key);
    }
  }
  if (storageAvailable) {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(CACHE_PREFIX)) {
          const cacheKey = storageKey.substring(CACHE_PREFIX.length);
          if (cacheKey === docsKey || cacheKey.startsWith(`${docsKey}?`)) {
            localStorage.removeItem(storageKey);
          }
        }
      }
    } catch {
      // Ignorieren
    }
  }
};

// ──────────────────────── Public Service API ─────────────────────────────

const loanCacheService = {

  // ── Read (gecacht) ────────────────────────────────────────────────────

  /**
   * Lädt alle Darlehen (gecacht, 15min TTL).
   * @param {object} [params] - Filter: page, size, sort, propertyId
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} Page<LoanDTO>
   */
  getLoans: (params, signal) =>
    getCached('/loans', params, signal, memoryCache, pendingRequests, CACHE_PREFIX, CACHE_TTL_MS, loansTokenRef),

  /**
   * Lädt ein einzelnes Darlehen (gecacht).
   * @param {string} id
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} LoanDTO
   */
  getLoan: (id, signal) =>
    getCached(`/loans/${id}`, undefined, signal, memoryCache, pendingRequests, CACHE_PREFIX, CACHE_TTL_MS, loansTokenRef),

  /**
   * Lädt Zahlungstermine eines Darlehens (gecacht, 5min TTL).
   * @param {string} loanId
   * @param {object} [params] - page, size, sort
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} Page<LoanPaymentDTO>
   */
  getPayments: (loanId, params, signal) =>
    getCached(`/loans/${loanId}/payments`, params, signal, paymentsMemoryCache, paymentsPendingRequests, PAYMENTS_CACHE_PREFIX, PAYMENTS_TTL_MS, paymentsTokenRef),

  /**
   * Lädt Dokumente eines Darlehens (gecacht, 15min TTL).
   * @param {string} loanId
   * @param {object} [params] - page, size
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} Page<LoanDocumentDTO>
   */
  getDocuments: (loanId, params, signal) =>
    getCached(`/loans/${loanId}/documents`, params, signal, memoryCache, pendingRequests, CACHE_PREFIX, CACHE_TTL_MS, loansTokenRef),

  // ── Mutationen ────────────────────────────────────────────────────────

  /**
   * Erstellt ein neues Darlehen.
   * @param {object} request - LoanRequest
   * @returns {Promise<object>} LoanDTO
   */
  createLoan: async (request) => {
    const response = await apiClient.post('/loans', request);
    invalidateLoansList();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Aktualisiert ein Darlehen.
   * @param {string} id
   * @param {object} request - LoanRequest
   * @returns {Promise<object>} LoanDTO
   */
  updateLoan: async (id, request) => {
    const response = await apiClient.put(`/loans/${id}`, request);
    invalidateLoansList();
    invalidateLoansDetail(id);
    invalidateLoanPayments(id);
    objektkostenService.invalidateAll();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Löscht ein Darlehen.
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteLoan: async (id) => {
    await apiClient.delete(`/loans/${id}`);
    invalidateLoansList();
    invalidateLoansDetail(id);
    invalidateLoanPayments(id);
    invalidateLoanDocuments(id);
    objektkostenService.invalidateAll();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
  },

  /**
   * Generiert den Tilgungsplan für ein Darlehen.
   * @param {string} id
   * @param {object} [body] - { startDatum?, laufzeitMonate? }
   * @returns {Promise<Array>} List<LoanPaymentDTO>
   */
  generatePaymentPlan: async (id, body = {}) => {
    const response = await apiClient.post(`/loans/${id}/generate-payment-plan`, body);
    invalidateLoanPayments(id);
    objektkostenService.invalidateAll();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Erstellt einen Zahlungseintrag.
   * @param {string} loanId
   * @param {object} request - LoanPaymentRequest
   * @returns {Promise<object>} LoanPaymentDTO
   */
  createPayment: async (loanId, request) => {
    let response;
    try {
      response = await apiClient.post(`/loans/${loanId}/payments`, request);
    } catch (err) {
      invalidatePaymentsOnConflict(loanId, err);
      throw err;
    }
    invalidateLoanPayments(loanId);
    objektkostenService.invalidateAll();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Aktualisiert einen Zahlungseintrag.
   * @param {string} loanId
   * @param {string} paymentId
   * @param {object} request - LoanPaymentRequest
   * @returns {Promise<object>} LoanPaymentDTO
   */
  updatePayment: async (loanId, paymentId, request) => {
    let response;
    try {
      response = await apiClient.put(`/loans/${loanId}/payments/${paymentId}`, request);
    } catch (err) {
      invalidatePaymentsOnConflict(loanId, err);
      throw err;
    }
    invalidateLoanPayments(loanId);
    objektkostenService.invalidateAll();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Löscht einen Zahlungseintrag.
   * @param {string} loanId
   * @param {string} paymentId
   * @returns {Promise<void>}
   */
  deletePayment: async (loanId, paymentId) => {
    try {
      await apiClient.delete(`/loans/${loanId}/payments/${paymentId}`);
    } catch (err) {
      invalidatePaymentsOnConflict(loanId, err);
      throw err;
    }
    invalidateLoanPayments(loanId);
    objektkostenService.invalidateAll();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
  },

  /**
   * Lädt ein Dokument hoch.
   * @param {string} loanId
   * @param {FormData} formData - enthält 'file'
   * @returns {Promise<object>} LoanDocumentDTO
   */
  uploadDocument: async (loanId, formData) => {
    const response = await apiClient.post(`/loans/${loanId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    invalidateLoanDocuments(loanId);
    return response.data;
  },

  /**
   * Löscht ein Dokument.
   * @param {string} loanId
   * @param {string} documentId
   * @returns {Promise<void>}
   */
  deleteDocument: async (loanId, documentId) => {
    await apiClient.delete(`/loans/${loanId}/documents/${documentId}`);
    invalidateLoanDocuments(loanId);
  },

  // ── Invalidations (für externe Aufrufer) ─────────────────────────────

  /** Invalidiert den List-Cache. */
  invalidateList: invalidateLoansList,

  /** Invalidiert den Detail-Cache für ein Darlehen. */
  invalidateDetail: invalidateLoansDetail,

  /** Invalidiert den Payments-Cache für ein Darlehen. */
  invalidatePayments: invalidateLoanPayments,

  /** Invalidiert den Documents-Cache für ein Darlehen. */
  invalidateDocuments: invalidateLoanDocuments,

  /**
   * Löscht den kompletten Cache (bei Logout).
   * Entfernt sowohl loans:v1:* als auch loan-payments:v1:* aus localStorage.
   */
  clearCache: () => {
    cacheInvalidationToken++;
    paymentsCacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    paymentsMemoryCache.clear();
    paymentsPendingRequests.clear();
    clearLocalStorageCacheByPrefix(CACHE_PREFIX);
    clearLocalStorageCacheByPrefix(PAYMENTS_CACHE_PREFIX);
  }
};

export default loanCacheService;
